import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import cx from 'classnames'
import { IEmpScale, IEmpScaleComment } from '@/models'
import { Button, Icon, Rate, Text, Textarea } from '@/components/ui'
import s from './question-types.module.scss'

interface ICommentsParams {
  id?: number
  answer: string
}

interface IFormValues {
  user_uuid: string
  value: number
  comments_params?: ICommentsParams[]
}

interface IEmpScaleQuestionProps {
  question: IEmpScale
  sendQuestion: (data: any) => void
}

const EmpScaleQuestion = ({ question, sendQuestion }: IEmpScaleQuestionProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<IFormValues[] | []>([])
  const [userIndex, setUserIndex] = useState<number>(0)
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<IFormValues>()
  const usersLength: number = question?.users?.length

  useFieldArray({ control, name: 'comments_params' })

  const onSubmit = (data: IFormValues) => {
    if (!usersLength) return sendQuestion({ users_params: [] })

    setSelectedAnswers((prev: IFormValues[] | []) => [
      ...prev,
      {
        user_uuid: question.users[userIndex]?.uuid,
        value: question.answers?.[data.value - 1]?.value,
        comments_params:
          data?.comments_params?.map((v: ICommentsParams, i: number) => ({ ...v, id: question?.comments[i]?.id })) || []
      }
    ])
    setUserIndex(userIndex + 1)
    reset()
  }

  useEffect(() => {
    if (usersLength && userIndex >= usersLength) {
      sendQuestion({ users_params: selectedAnswers })
    }
    // TODO: Проверить зависимости и включить правило
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIndex, usersLength])

  useEffect(() => {
    if (question) {
      setUserIndex(0)
      setSelectedAnswers([])
    }
  }, [question])

  if (!question) return null

  return (
    <div className={cx(s['question-wrapper'], s['emp-scale-question'])}>
      <form className={s.question} onSubmit={handleSubmit(onSubmit)}>
        {usersLength ? (
          <>
            <Text as='p' size='xxl' weight='semibold'>
              {question.users[userIndex]?.title}
            </Text>
            <Text as='p' size='xs' color='gray-dark'>
              {`Сотрудник ${userIndex + 1} из ${question.users.length}`}
            </Text>
            <Controller
              name='value'
              control={control}
              rules={{
                required: { value: true, message: 'Выберите оценку' }
              }}
              render={({ field: { name, value, onChange } }) => (
                <Rate
                  classNameWrapper='offset-top-20'
                  name={name}
                  value={Number(value)}
                  onChange={onChange}
                  count={question.answers.length}
                  character={<Icon name='star' />}
                  errors={errors}
                />
              )}
            />
            {!!(question?.ask_text_comments && question?.comments?.length) && (
              <div className='offset-top-30'>
                <Text as='p' size='sm'>
                  Ответьте на комментарии ниже:
                </Text>
                <div className='offset-top-20'>
                  {question.comments.map((v: IEmpScaleComment, i: number) => (
                    <div key={v.id} className={s['comment-item']}>
                      <Controller
                        name={`comments_params.${i}.answer`}
                        control={control}
                        rules={{
                          required: { value: true, message: 'Не указан комментарий' }
                        }}
                        render={({ field: { value, onChange } }) => (
                          <Textarea
                            name='answer'
                            value={value || ''}
                            onChange={onChange}
                            label={v.title}
                            errors={errors.comments_params?.[i]}
                            view='primary'
                            fluid
                            autoHeight
                          />
                        )}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <Text as='p' size='md'>
            Нет сотрудников для оценки. Можете продолжить опрос
          </Text>
        )}
        <Button type='submit' className={s['question__next-button']}>
          Продолжить
        </Button>
      </form>
    </div>
  )
}

export default EmpScaleQuestion
